import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer, { GOOGLE_BUSINESS_URL } from "../components/Footer";
import SEO from "../components/SEO";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  Truck,
  Compass,
  Star,
  MapPin,
  Zap,
  ShieldCheck,
  TrendingUp,
  Route,
  ArrowRight,
  CheckCircle2,
  Euro,
  Clock,
  Bell,
  Quote,
  ExternalLink,
  Calendar,
  Wrench,
  Building2,
  Fuel,
  BatteryCharging,
  CircleDot,
  Car,
  CarFront,
  Bike,
  Gem,
  Construction,
} from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1730514785075-b065c757b653?fm=jpg&q=85&w=2000&auto=format&fit=crop";
const MAP_IMG = "https://images.pexels.com/photos/4048104/pexels-photo-4048104.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const DRIVER_IMG =
  "https://images.unsplash.com/photo-1760944016562-35f97c8c34e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBtZWNoYW5pYyUyMHRydWNrJTIwZHJpdmVyJTIwcG9ydHJhaXR8ZW58MHx8fHwxNzc2NjEyMzc0fDA&ixlib=rb-4.1.0&q=85";
const NIGHT_IMG =
  "https://images.unsplash.com/photo-1641558024625-5906eae28712?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxoaWdod2F5JTIwbmlnaHQlMjB0cmFja2luZ3xlbnwwfHx8fDE3NzY2MTIzODV8MA&ixlib=rb-4.1.0&q=85";

function Pill({ children }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-orange-700 bg-orange-100 rounded-full border border-orange-200">
      {children}
    </span>
  );
}

export default function Landing() {
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/public/stats").then((r) => r.data).catch(() => null),
      api.get("/public/reviews", { params: { limit: 6, min_rating: 4 } }).then((r) => r.data).catch(() => []),
    ]).then(([s, r]) => {
      setStats(s);
      setReviews(r);
    });
  }, []);

  const jsonLd = stats && stats.rating_count > 0 ? {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "DepannRoute",
    "url": "https://depannroute.com",
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": stats.rating_avg,
      "reviewCount": stats.rating_count,
      "bestRating": 5,
      "worstRating": 1,
    },
  } : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="landing-page">
      <SEO
        title="DepannRoute — Dépannage & remorquage à la demande | FR · BE · ES · DE"
        description="DepannRoute met en relation usagers de la route et dépanneurs/remorqueurs professionnels. Panne, accident, transport de véhicule, dépannage sur place (roue, batterie, carburant). Disponible 24h/24, géolocalisation temps réel. France, Belgique, Espagne, Allemagne."
        canonical="https://depannroute.com/"
        jsonLd={jsonLd}
      />
      <Header variant="marketing" />

      {/* HERO */}
      <section className="pt-24 lg:pt-28 pb-20 lg:pb-28 relative overflow-hidden">
        <div className="absolute inset-0 -z-10 dot-grid opacity-60" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-6">
              <span className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-slate-900">
                <span className="live-dot" /> Plateforme opérationnelle
              </span>
              <span className="hidden sm:inline text-xs text-slate-500 font-medium">FR · BE · ES · DE</span>
            </div>

            <h1 className="font-display font-extrabold tracking-tighter text-slate-900 leading-[0.95] text-4xl sm:text-5xl lg:text-6xl">
              Dépannage &amp; remorquage
              <br />
              <span className="underline-ink">à la demande.</span>
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-slate-600 font-normal max-w-2xl leading-relaxed">
              DepannRoute met en relation les usagers de la route avec des dépanneurs et remorqueurs professionnels certifiés.
              Panne, accident, déplacement ou rapatriement de votre véhicule —
              <span className="font-semibold text-slate-900"> disponible en temps réel, 24h/24.</span>
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-3" data-testid="hero-ctas">
              <Link to="/register?role=client" data-testid="cta-client">
                <Button className="w-full sm:w-auto bg-orange-600 hover:bg-orange-700 text-white rounded-md px-7 py-6 text-base font-semibold">
                  <Zap className="mr-2 h-5 w-5" />
                  Je suis client · Publier une demande
                </Button>
              </Link>
              <Link to="/register?role=pro" data-testid="cta-pro">
                <Button variant="outline" className="w-full sm:w-auto border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-md px-7 py-6 text-base font-semibold">
                  <Truck className="mr-2 h-5 w-5" />
                  Je suis dépanneur · Accéder au marché
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl border-t border-slate-200 pt-6">
              <div>
                <div className="font-display font-extrabold text-3xl text-slate-900">-37%</div>
                <div className="text-xs text-slate-500 mt-1 uppercase tracking-wide">Trajets à vide</div>
              </div>
              <div>
                <div className="font-display font-extrabold text-3xl text-slate-900">+32%</div>
                <div className="text-xs text-slate-500 mt-1 uppercase tracking-wide">CA dépanneur</div>
              </div>
              <div>
                <div className="font-display font-extrabold text-3xl text-slate-900">-22%</div>
                <div className="text-xs text-slate-500 mt-1 uppercase tracking-wide">Prix client</div>
              </div>
              <div>
                <div className="font-display font-extrabold text-3xl text-slate-900">10%</div>
                <div className="text-xs text-slate-500 mt-1 uppercase tracking-wide">Commission only</div>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-600 max-w-2xl" data-testid="hero-savings">
              <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> <strong className="text-slate-900">0 €</strong> d'abonnement</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Paiement sécurisé Stripe</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Aucun frais caché</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Facturation automatique</span>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-md overflow-hidden border border-slate-200 shadow-xl">
              <img src={HERO_IMG} alt="Tow truck on highway" className="w-full h-[440px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/75 via-slate-900/25 to-transparent" />
              <div className="absolute top-4 left-4 right-4 flex justify-between items-start">
                <div className="bg-white/95 backdrop-blur px-3 py-2 rounded-md shadow-sm">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Mission #4182</div>
                  <div className="text-sm font-bold text-slate-900">Lyon → Paris · Berline</div>
                </div>
                <span className="bg-orange-600 text-white text-[11px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider">
                  Urgence
                </span>
              </div>
              <div className="absolute bottom-4 left-4 right-4 grid grid-cols-2 gap-2">
                <div className="bg-white/95 backdrop-blur px-3 py-2 rounded-md">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-bold"><Clock className="h-3 w-3" /> ETA</div>
                  <div className="text-sm font-bold text-slate-900">42 min</div>
                </div>
                <div className="bg-orange-600 text-white px-3 py-2 rounded-md">
                  <div className="text-[10px] uppercase tracking-widest font-bold opacity-90">Retour matché</div>
                  <div className="text-sm font-bold">Paris → Lyon +</div>
                </div>
              </div>
            </div>
            <div className="absolute -bottom-6 -left-6 bg-slate-900 text-white px-5 py-3 rounded-md shadow-lg hidden sm:block">
              <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">Score matching</div>
              <div className="font-display font-bold text-2xl">94/100</div>
            </div>
          </div>
        </div>
      </section>

      {/* URGENCE CLIENT — storytelling rassurant */}
      <section className="py-16 bg-orange-600 text-white relative overflow-hidden">
        <div className="absolute inset-0 safety-stripes opacity-[0.07]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-orange-200 text-sm font-bold uppercase tracking-widest mb-4">
              <span className="live-dot bg-white" /> Vous êtes en panne en ce moment ?
            </div>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Un dépanneur près de vous en quelques minutes.
            </h2>
            <p className="mt-4 text-orange-100 text-lg max-w-2xl mx-auto">
              Restez calme. DepannRoute trouve pour vous un partenaire disponible, proche de votre position, prêt à intervenir.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { n: "1", icon: MapPin, title: "Localisez-vous", desc: "Indiquez votre position et votre destination. La géolocalisation fait le reste." },
              { n: "2", icon: Truck, title: "Recevez des offres", desc: "Des dépanneurs certifiés proches de vous répondent en temps réel." },
              { n: "3", icon: CheckCircle2, title: "Choisissez votre pro", desc: "Comparez les offres, les avis, les prix. Acceptez en un clic." },
              { n: "4", icon: Route, title: "Reprenez la route", desc: "Suivez votre mission en temps réel. Payez en ligne, facture automatique." },
            ].map(({ n, icon: Icon, title, desc }) => (
              <div key={n} className="bg-white/10 backdrop-blur border border-white/20 rounded-md p-6 hover:bg-white/20 transition-colors">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-7 h-7 rounded-full bg-white text-orange-600 font-display font-extrabold text-sm flex items-center justify-center flex-shrink-0">{n}</span>
                  <Icon className="h-6 w-6 text-white/90" strokeWidth={1.8} />
                </div>
                <div className="font-display font-bold text-lg mb-1">{title}</div>
                <div className="text-orange-100 text-sm leading-relaxed">{desc}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link to="/register?role=client">
              <Button className="bg-white text-orange-600 hover:bg-orange-50 font-display font-bold px-8 py-6 text-base rounded-md shadow-lg">
                <Zap className="mr-2 h-5 w-5" /> Je cherche un dépanneur maintenant
              </Button>
            </Link>
            <div className="mt-4 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-orange-200">
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Gratuit pour le client</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Disponible 24h/24 · 7j/7</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Paiement sécurisé en ligne</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> Pros certifiés &amp; assurés</span>
            </div>
          </div>
        </div>
      </section>

      {/* DUAL CARDS */}
      <section className="py-16 sm:py-24 bg-white border-y border-slate-200" id="how">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-12 max-w-3xl">
            <Pill><MapPin className="h-3 w-3" /> Comment ça marche</Pill>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 mt-4">
              Deux côtés du marché. Un seul moteur.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-4 lg:gap-6" data-testid="dual-cards">
            {/* Client */}
            <div className="bg-slate-50 border border-slate-200 rounded-md p-8 relative overflow-hidden group hover:-translate-y-0.5 transition-transform">
              <div className="absolute top-0 left-0 right-0 h-1 bg-orange-600" />
              <Pill>Client · usager de la route</Pill>
              <h3 className="font-display font-bold text-2xl sm:text-3xl mt-4 text-slate-900">Suite à une panne ou un accident</h3>
              <p className="text-slate-600 mt-2">Voiture, 4x4, moto, utilitaire, véhicule de collection ou de prestige — trouvez le bon partenaire proche de vous en quelques minutes.</p>
              <ul className="mt-6 space-y-3">
                <li className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-orange-600 mt-0.5" /><span className="text-sm text-slate-700"><strong>Commande immédiate</strong> — grâce à la géolocalisation, un dépanneur disponible proche de vous.</span></li>
                <li className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-orange-600 mt-0.5" /><span className="text-sm text-slate-700"><strong>Réservation planifiée</strong> — organisez le transport de votre véhicule au moment souhaité.</span></li>
                <li className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-orange-600 mt-0.5" /><span className="text-sm text-slate-700"><strong>Avis vérifiés</strong> — chaque partenaire noté post-mission, aucune surprise.</span></li>
              </ul>
              <Link to="/register?role=client">
                <Button className="mt-8 w-full bg-orange-600 hover:bg-orange-700 text-white rounded-md" data-testid="cta-block-client">
                  Publier une demande <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            {/* Pro */}
            <div className="bg-slate-900 text-white rounded-md p-8 relative overflow-hidden group hover:-translate-y-0.5 transition-transform">
              <div className="absolute inset-0 safety-stripes opacity-[0.06]" />
              <div className="relative">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-orange-400 border border-orange-400/40 rounded-full">
                  Dépanneur · remorqueur pro
                </span>
                <h3 className="font-display font-bold text-2xl sm:text-3xl mt-4">Je suis dépanneur</h3>
                <p className="text-slate-300 mt-2">Des milliers de missions disponibles. Optimisez vos itinéraires et boostez votre chiffre d'affaires.</p>
                <ul className="mt-6 space-y-3">
                  <li className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-orange-400 mt-0.5" /><span className="text-sm text-slate-200"><strong>Matching retour auto</strong> — une mission retour sur le même axe, zéro trajet à vide.</span></li>
                  <li className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-orange-400 mt-0.5" /><span className="text-sm text-slate-200"><strong>Itinéraires optimisés</strong> — géolocalisation temps réel, missions proches de vous.</span></li>
                  <li className="flex items-start gap-3"><CheckCircle2 className="h-5 w-5 text-orange-400 mt-0.5" /><span className="text-sm text-slate-200"><strong>Dashboard rentabilité</strong> — CA, taux d'acceptation, notation clients.</span></li>
                </ul>
                <Link to="/register?role=pro">
                  <Button variant="outline" className="mt-8 w-full border-2 border-white text-white hover:bg-white hover:text-slate-900 rounded-md" data-testid="cta-block-pro">
                    Accéder au marché <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MATCHING BLOCK */}
      <section className="py-20 sm:py-28" id="matching">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Pill><Compass className="h-3 w-3" /> Moteur de matching</Pill>
              <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 mt-4">
                Matching aller/retour unique en Europe.
              </h2>
              <p className="mt-4 text-lg text-slate-600 max-w-xl leading-relaxed">
                Notre algorithme combine <strong>deux missions opposées</strong> sur un axe géographique
                pour maximiser la rentabilité. Fini les trajets à vide.
              </p>
              <div className="mt-8 grid sm:grid-cols-3 gap-4">
                <div className="bg-white border border-slate-200 rounded-md p-5">
                  <Route className="h-5 w-5 text-orange-600" />
                  <div className="font-display font-bold text-xl mt-2 text-slate-900">Distance</div>
                  <div className="text-xs text-slate-500 mt-0.5">Haversine km</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-md p-5">
                  <Compass className="h-5 w-5 text-orange-600" />
                  <div className="font-display font-bold text-xl mt-2 text-slate-900">Axe</div>
                  <div className="text-xs text-slate-500 mt-0.5">Cap aller/retour</div>
                </div>
                <div className="bg-white border border-slate-200 rounded-md p-5">
                  <Clock className="h-5 w-5 text-orange-600" />
                  <div className="font-display font-bold text-xl mt-2 text-slate-900">Timing</div>
                  <div className="text-xs text-slate-500 mt-0.5">Fenêtre 48h</div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="rounded-md overflow-hidden border border-slate-200 shadow-lg bg-white">
                <img src={MAP_IMG} alt="European routing lines" className="w-full h-[420px] object-cover" />
              </div>
              <div className="absolute top-6 right-6 bg-slate-900 text-white px-4 py-3 rounded-md shadow-lg">
                <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">Économie moyenne</div>
                <div className="font-display font-bold text-2xl">+32% CA</div>
              </div>
              <div className="absolute bottom-6 left-6 bg-white border border-slate-200 px-4 py-3 rounded-md shadow-lg">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Paris ↔ Lyon</div>
                <div className="font-display font-bold text-slate-900">Score 94 · A/R</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES SUR PLACE */}
      <section className="py-20 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Pill><Wrench className="h-3 w-3" /> La solution sur place</Pill>
              <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-900 mt-4">
                Pas toujours besoin du plateau. La solution existe !
              </h2>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                Nos partenaires dépanneurs peuvent intervenir directement sur place pour vous remettre en route
                <span className="font-semibold text-slate-900"> en toute sérénité</span>, sans remorquage.
              </p>
              <div className="mt-8 grid sm:grid-cols-3 gap-4">
                <div className="bg-slate-50 border border-slate-200 rounded-md p-5 text-center">
                  <CircleDot className="h-7 w-7 text-orange-600 mx-auto" />
                  <div className="font-display font-bold text-slate-900 mt-3 text-sm">Changement de roue</div>
                  <div className="text-xs text-slate-500 mt-1">Crevaison sur la route</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-md p-5 text-center">
                  <Fuel className="h-7 w-7 text-orange-600 mx-auto" />
                  <div className="font-display font-bold text-slate-900 mt-3 text-sm">Dépannage carburant</div>
                  <div className="text-xs text-slate-500 mt-1">Approvisionnement sur place</div>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-md p-5 text-center">
                  <BatteryCharging className="h-7 w-7 text-orange-600 mx-auto" />
                  <div className="font-display font-bold text-slate-900 mt-3 text-sm">Boost batterie</div>
                  <div className="text-xs text-slate-500 mt-1">Démarrage immédiat</div>
                </div>
              </div>
              <Link to="/register?role=client" className="mt-8 inline-block">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-md px-6 py-5">
                  Trouver un dépanneur proche <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="bg-slate-900 rounded-md p-8 text-white relative overflow-hidden">
              <div className="absolute inset-0 safety-stripes opacity-[0.05]" />
              <div className="relative space-y-5">
                <div className="text-[10px] uppercase tracking-widest text-orange-400 font-bold">Types de véhicules pris en charge</div>
                {[
                  { icon: Car, label: "Voiture VL / Berline", desc: "Toutes cylindrées, toutes marques" },
                  { icon: CarFront, label: "SUV & 4x4", desc: "Véhicules lourds et utilitaires ≤ 3,5t" },
                  { icon: Bike, label: "Moto & Scooter", desc: "Deux-roues motorisés" },
                  { icon: Gem, label: "Collection & Prestige", desc: "Transport fermé, manipulation gants blancs" },
                  { icon: Construction, label: "Engins de chantier", desc: "Sur devis, transport spécialisé" },
                ].map(({ icon: Icon, label, desc }) => (
                  <div key={label} className="flex items-center gap-4 border-b border-white/10 pb-4 last:border-0 last:pb-0">
                    <span className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5 text-orange-400" strokeWidth={1.8} />
                    </span>
                    <div>
                      <div className="font-display font-bold text-sm">{label}</div>
                      <div className="text-xs text-slate-400">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* RÉSERVATION PLANIFIÉE */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 bg-orange-50 border border-orange-200 rounded-md p-8">
              <div className="space-y-4">
                {[
                  { step: "1", title: "Choisissez votre date", desc: "Planifiez à l'avance — départ, arrivée, type de véhicule." },
                  { step: "2", title: "Recevez des offres", desc: "Des milliers de dépanneurs/remorqueurs prêts à intervenir." },
                  { step: "3", title: "Confirmez & payez", desc: "Paiement sécurisé, mission confirmée, tranquillité d'esprit." },
                ].map(({ step, title, desc }) => (
                  <div key={step} className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-orange-600 text-white font-display font-extrabold text-sm flex items-center justify-center flex-shrink-0">
                      {step}
                    </div>
                    <div>
                      <div className="font-display font-bold text-slate-900">{title}</div>
                      <div className="text-sm text-slate-600 mt-0.5">{desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <Pill><Calendar className="h-3 w-3" /> Réservation planifiée</Pill>
              <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-900 mt-4">
                Planifiez le transport de votre véhicule.
              </h2>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                Pas d'urgence ? Organisez le déplacement ou le rapatriement de votre véhicule
                <strong className="text-slate-900"> au moment et à l'endroit souhaités</strong>.
                Des milliers de dépanneurs et remorqueurs sont prêts à vous prendre en charge.
              </p>
              <div className="mt-6 flex flex-wrap gap-3 text-sm text-slate-700">
                <span className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2">
                  <Clock className="h-4 w-4 text-orange-600" /> Disponibilité en temps réel
                </span>
                <span className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2">
                  <MapPin className="h-4 w-4 text-orange-600" /> Géolocalisation précise
                </span>
                <span className="flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2">
                  <Route className="h-4 w-4 text-orange-600" /> Itinéraires optimisés
                </span>
              </div>
              <Link to="/register?role=client" className="mt-8 inline-block">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-md px-6 py-5">
                  Planifier un transport <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* COMPTE PROFESSIONNEL */}
      <section className="py-20 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 safety-stripes opacity-[0.04]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-orange-400 border border-orange-400/40 rounded-full">
                <Building2 className="h-3 w-3" /> Compte professionnel
              </span>
              <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight mt-4">
                Garagistes, concessionnaires, loueurs — DepannRoute vous accompagne.
              </h2>
              <p className="mt-4 text-lg text-slate-300 leading-relaxed">
                Vous gérez une flotte ou avez des besoins récurrents de transport de véhicules ?
                DepannRoute propose un <strong className="text-white">compte professionnel dédié</strong> pour
                piloter tous vos remorquages et transports depuis un seul tableau de bord.
              </p>
              <ul className="mt-8 space-y-3">
                {[
                  "Gestion multi-véhicules depuis un tableau de bord unique",
                  "Historique complet des missions et facturation automatique",
                  "Tarifs préférentiels sur volume et accès prioritaire aux pros",
                  "Idéal pour garages, concessionnaires et loueurs de voitures",
                ].map(item => (
                  <li key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-orange-400 mt-0.5 flex-shrink-0" />
                    <span className="text-slate-200 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/register?role=client" className="mt-8 inline-block">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-md px-6 py-5">
                  Créer un compte professionnel <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: "🏢", label: "Garagistes", desc: "Rapatriement des véhicules clients après réparation" },
                { icon: "🚘", label: "Concessionnaires", desc: "Transport de véhicules neufs ou d'occasion" },
                { icon: "🔑", label: "Loueurs", desc: "Récupération de véhicules en panne ou mal garés" },
                { icon: "🏗", label: "Entreprises", desc: "Transport d'engins et de véhicules de société" },
              ].map(({ icon, label, desc }) => (
                <div key={label} className="bg-white/5 border border-white/10 rounded-md p-5 hover:bg-white/10 transition-colors">
                  <div className="text-3xl mb-3">{icon}</div>
                  <div className="font-display font-bold text-sm">{label}</div>
                  <div className="text-xs text-slate-400 mt-1 leading-relaxed">{desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="py-20 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <Pill><ShieldCheck className="h-3 w-3" /> Tout en un</Pill>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-900 mt-4 max-w-3xl">
              Tout ce qu'il faut pour un marché du dépannage fiable.
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="features-bento">
            <Feature icon={MapPin} title="Géolocalisation temps réel" body="Un partenaire dépanneur disponible, proche de vous, détecté automatiquement." />
            <Feature icon={Star} title="Avis certifiés" body="Notation post-mission pour garantir la qualité. Aucun dépanneur ne peut modifier un avis." />
            <Feature icon={Bell} title="Notifications instantanées" body="Suivez votre mission en temps réel — offres, ETA, paiements." />
            <Feature icon={TrendingUp} title="Dashboard rentabilité" body="CA, taux d'acceptation, km économisés. Pour les pros." />
            <Feature icon={Calendar} title="Réservation planifiée" body="Organisez le transport de votre véhicule au moment et à l'endroit souhaités." />
            <Feature icon={ShieldCheck} title="Pros certifiés" body="Dépanneurs et remorqueurs vérifiés — assurance RC Pro, certifications, notes." />
            <Feature icon={Route} title="Matching aller/retour" body="Notre algorithme combine deux missions sur un axe — zéro trajet à vide." />
            <Feature icon={Building2} title="Compte professionnel" body="Garagistes, concessionnaires, loueurs — pilotez tous vos transports depuis un seul espace." />
          </div>
        </div>
      </section>

      {/* ECONOMIES */}
      <section className="py-20 bg-slate-900 text-white relative overflow-hidden" id="economies">
        <div className="absolute inset-0 safety-stripes opacity-[0.04]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.18em] text-orange-400 border border-orange-400/40 rounded-full">
              <Euro className="h-3 w-3" /> Économies concrètes
            </span>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight mt-4">
              Ce que DepannRoute vous <span className="text-orange-400">fait gagner</span>.
            </h2>
            <p className="mt-3 text-slate-300 text-lg max-w-2xl">
              Chiffres observés sur un trajet longue distance type (ex : Paris ↔ Lyon, 460 km).
            </p>
          </div>

          <div className="mt-12 grid md:grid-cols-2 gap-4 lg:gap-6" data-testid="economies-grid">
            {/* CLIENT */}
            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-md p-6 lg:p-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-orange-400">Côté client</span>
                <span className="text-xs text-slate-400">Moyenne constatée</span>
              </div>
              <div className="space-y-4">
                <EcoRow old="450 €" new="350 €" saving="-100 €" label="Remorquage longue distance (moyenne marché vs. DepannRoute)" />
                <EcoRow old="2-3h" new="42 min" saving="Réactivité" label="Délai moyen pour obtenir une offre ferme" />
                <EcoRow old="1 seul pro" new="3 à 5 offres" saving="Choix" label="Mise en concurrence transparente" />
                <EcoRow old="Devis papier" new="Facture auto" saving="0 frais" label="Facturation & paiement sécurisés en ligne" />
              </div>
              <div className="mt-6 pt-6 border-t border-white/10 flex items-baseline justify-between">
                <span className="text-sm text-slate-300">Économie moyenne par mission</span>
                <span className="font-display font-extrabold text-3xl text-orange-400">-22 %</span>
              </div>
              <Link to="/register?role=client" className="mt-5 inline-block">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-md" data-testid="eco-cta-client">
                  Publier ma demande <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            {/* PRO */}
            <div className="bg-white text-slate-900 rounded-md p-6 lg:p-8 relative">
              <div className="absolute -top-3 right-6 bg-orange-600 text-white text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full">Top ROI</div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-[11px] uppercase tracking-[0.18em] font-bold text-orange-600">Côté dépanneur</span>
                <span className="text-xs text-slate-500">Par rotation A/R</span>
              </div>
              <div className="space-y-4">
                <EcoRow light old="1 mission" new="2 missions" saving="x2" label="Missions réalisées sur la même rotation grâce au matching retour" />
                <EcoRow light old="460 km" new="920 km" saving="x2 km facturés" label="Kilométrage facturé pour une rotation inchangée" />
                <EcoRow light old="50 L vide" new="0 L vide" saving="-45 €" label="Carburant économisé sur le trajet retour (moyenne diesel)" />
                <EcoRow light old="20% frais plateformes" new="10% DepannRoute" saving="-50 %" label="Commission divisée par deux vs. concurrents" />
              </div>
              <div className="mt-6 pt-6 border-t border-slate-200 flex items-baseline justify-between">
                <span className="text-sm text-slate-600">CA additionnel moyen</span>
                <span className="font-display font-extrabold text-3xl text-orange-600">+32 %</span>
              </div>
              <Link to="/register?role=pro" className="mt-5 inline-block">
                <Button variant="outline" className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-md" data-testid="eco-cta-pro">
                  Accéder au marché <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-400">
            <span>* Données observées sur un panel de 120+ missions longue distance en 2025.</span>
            <span>· 0 € d'abonnement · Commission 10 % uniquement sur missions facturées.</span>
          </div>
        </div>
      </section>

      {/* COVERAGE */}
      <section className="py-20" id="coverage">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Pill>Couverture</Pill>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-900 mt-4">
              4 pays, 50+ villes, un seul marché.
            </h2>
            <p className="mt-4 text-slate-600 text-lg">France, Belgique, Espagne, Allemagne. Nos pros couvrent les axes autoroutiers majeurs et les zones urbaines denses.</p>

            <div className="mt-8 grid grid-cols-2 gap-3" data-testid="coverage-grid">
              {[
                { flag: "🇫🇷", name: "France", cities: "Paris · Lyon · Marseille · Bordeaux · Lille" },
                { flag: "🇧🇪", name: "Belgique", cities: "Bruxelles · Anvers · Liège · Gand · Namur" },
                { flag: "🇪🇸", name: "Espagne", cities: "Madrid · Barcelone · Valence · Séville" },
                { flag: "🇩🇪", name: "Allemagne", cities: "Berlin · Munich · Hambourg · Cologne" },
              ].map((c) => (
                <div key={c.name} className="bg-white border border-slate-200 rounded-md p-4">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xl">{c.flag}</span>
                    <span className="font-display font-bold text-slate-900">{c.name}</span>
                  </div>
                  <div className="text-xs text-slate-500 leading-relaxed">{c.cities}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="relative rounded-md overflow-hidden border border-slate-200 shadow-lg">
            <img src={NIGHT_IMG} alt="Highway at night" className="w-full h-[460px] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/80 via-slate-900/30 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-white">
              <div className="text-[11px] uppercase tracking-[0.2em] text-orange-400 font-bold mb-2">Opérationnel 24/7</div>
              <div className="font-display font-bold text-3xl">Jamais un axe inactif.</div>
            </div>
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES CROISÉS client + pro */}
      <section className="py-16 bg-white border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <Pill><Quote className="h-3 w-3" /> Ils en parlent</Pill>
            <h2 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-900 mt-4">
              Des deux côtés de la route.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {/* Témoignage client */}
            <div className="bg-orange-50 border border-orange-200 rounded-md p-8">
              <div className="flex gap-1 mb-4">
                {[1,2,3,4,5].map(n => <Star key={n} className="h-5 w-5 fill-amber-500 text-amber-500" />)}
              </div>
              <blockquote className="font-display font-bold text-xl text-slate-900 leading-snug">
                "Panne sur l'autoroute à 22h, en famille. En 8 minutes j'avais une offre.
                <span className="text-orange-600"> Le dépanneur était là en 35 minutes</span>, professionnel et rassurant."
              </blockquote>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-600 text-white font-display font-extrabold flex items-center justify-center">SL</div>
                <div>
                  <div className="font-display font-bold text-slate-900">Sophie L.</div>
                  <div className="text-sm text-slate-500">Cliente particulière · A6, région Bourgogne</div>
                </div>
              </div>
            </div>
            {/* Témoignage pro */}
            <div className="bg-slate-900 text-white rounded-md p-8">
              <div className="flex gap-1 mb-4">
                {[1,2,3,4,5].map(n => <Star key={n} className="h-5 w-5 fill-orange-400 text-orange-400" />)}
              </div>
              <blockquote className="font-display font-bold text-xl leading-snug">
                "Depuis DepannRoute, je ne rentre plus à vide. Le matching retour me permet de
                <span className="text-orange-400"> doubler ma rentabilité</span> sur les longs trajets."
              </blockquote>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-500 text-white font-display font-extrabold flex items-center justify-center">KB</div>
                <div>
                  <div className="font-display font-bold">Karim B.</div>
                  <div className="text-sm text-slate-400">Dépanneur indépendant · Région PACA</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PUBLIC REVIEWS / GOOGLE */}
      <section className="py-20 bg-white border-y border-slate-200" id="reviews">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
            <div>
              <Pill><Star className="h-3 w-3" /> Avis certifiés</Pill>
              <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 mt-4">
                {stats && stats.rating_count > 0 ? (
                  <>
                    {stats.rating_avg}/5 — sur <span className="text-orange-600">{stats.rating_count} avis</span> vérifiés
                  </>
                ) : (
                  <>Des avis authentiques, post-mission.</>
                )}
              </h2>
              <p className="mt-3 text-slate-600 max-w-2xl">Chaque mission est notée par le client. Aucun dépanneur ne peut modifier ou supprimer un avis.</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <a href={GOOGLE_BUSINESS_URL} target="_blank" rel="noopener noreferrer" data-testid="landing-google-reviews">
                <Button variant="outline" className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-md">
                  <svg viewBox="0 0 48 48" className="mr-2 h-4 w-4" aria-hidden="true">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                  Voir avis Google
                  <ExternalLink className="ml-2 h-3.5 w-3.5" />
                </Button>
              </a>
              <Link to="/avis">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-md">
                  Tous les avis <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>

          {reviews.length === 0 ? (
            <div className="bg-slate-50 border border-dashed border-slate-300 rounded-md p-10 text-center">
              <Quote className="h-10 w-10 mx-auto text-slate-300" />
              <div className="font-display font-bold text-xl mt-3 text-slate-900">Les premiers avis arrivent bientôt</div>
              <p className="text-sm text-slate-600 mt-1">La plateforme est en phase de lancement. Les avis clients s'afficheront ici dès les premières missions.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="landing-reviews-grid">
              {reviews.slice(0, 6).map((r) => (
                <article key={r.review_id} className="bg-slate-50 border border-slate-200 rounded-md p-5" itemScope itemType="https://schema.org/Review">
                  <meta itemProp="datePublished" content={r.created_at} />
                  <div className="flex items-center gap-1 text-amber-500 mb-2" itemProp="reviewRating" itemScope itemType="https://schema.org/Rating">
                    <meta itemProp="ratingValue" content={r.rating} />
                    <meta itemProp="bestRating" content="5" />
                    {[1,2,3,4,5].map((n) => <Star key={n} className={`h-4 w-4 ${n <= r.rating ? "fill-amber-500" : "text-slate-300"}`} />)}
                  </div>
                  <Quote className="h-4 w-4 text-orange-300" />
                  <p className="mt-2 text-sm text-slate-700 line-clamp-5" itemProp="reviewBody">{r.comment || "—"}</p>
                  <div className="mt-4 pt-4 border-t border-slate-200 flex items-center gap-3" itemProp="author" itemScope itemType="https://schema.org/Person">
                    <div className="h-8 w-8 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center">
                      {(r.client_name || "?").split(" ").map(p=>p[0]).join("").slice(0,2).toUpperCase()}
                    </div>
                    <div className="text-xs text-slate-600">
                      <div className="font-semibold text-slate-900 text-sm" itemProp="name">{r.client_name || "Client"}</div>
                      {r.pro_company && <div>par <strong>{r.pro_company}</strong>{r.pro_city ? ` · ${r.pro_city}` : ""}</div>}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24" id="pricing">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Pill>Prêt à démarrer ?</Pill>
          <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-slate-900 mt-4">
            Reprenez la route en toute sérénité.
          </h2>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl mx-auto">
            Panne, accident ou simple déplacement de véhicule — DepannRoute vous connecte au bon professionnel, au bon moment.
            Inscription gratuite, commission 10% uniquement sur missions facturées.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register?role=client"><Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-md px-8 py-6 text-base font-semibold"><Zap className="mr-2 h-5 w-5" />Je cherche un dépanneur</Button></Link>
            <Link to="/register?role=pro"><Button variant="outline" className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-md px-8 py-6 text-base font-semibold"><Truck className="mr-2 h-5 w-5" />Je suis dépanneur / remorqueur</Button></Link>
          </div>
        </div>
      </section>

      <Footer stats={stats} />
    </div>
  );
}

function Feature({ icon: Icon, title, body }) {
  return (
    <div className="bg-slate-50 border border-slate-200 rounded-md p-5 hover:border-orange-600 hover:-translate-y-0.5 transition-all" data-testid={`feature-${title}`}>
      <div className="flex h-9 w-9 items-center justify-center bg-white border border-slate-200 rounded-md">
        <Icon className="h-4 w-4 text-orange-600" />
      </div>
      <div className="font-display font-bold text-slate-900 mt-4">{title}</div>
      <div className="text-xs text-slate-600 mt-1 leading-relaxed">{body}</div>
    </div>
  );
}

function EcoRow({ old: oldVal, new: newVal, saving, label, light = false }) {
  const labelCn = light ? "text-slate-600" : "text-slate-400";
  const strikeCn = light ? "text-slate-400 line-through" : "text-slate-500 line-through";
  const newCn = light ? "text-slate-900 font-bold" : "text-white font-bold";
  const chipCn = light ? "bg-orange-100 text-orange-700" : "bg-orange-500/15 text-orange-300";
  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`text-sm ${strikeCn}`}>{oldVal}</span>
          <ArrowRight className={`h-3.5 w-3.5 ${light ? "text-slate-400" : "text-slate-500"}`} />
          <span className={`text-sm ${newCn}`}>{newVal}</span>
        </div>
        <div className={`text-xs mt-1 leading-relaxed ${labelCn}`}>{label}</div>
      </div>
      <span className={`shrink-0 inline-flex items-center rounded-full px-2 py-1 text-[10px] font-bold uppercase tracking-wider ${chipCn}`}>
        {saving}
      </span>
    </div>
  );
}
